<script setup lang="ts">
import type { StorePromoCoupon } from "~~/shared/types/store";
import { formatCouponReduction } from "~~/shared/utils/couponLabel";

const props = defineProps<{
  coupon?: StorePromoCoupon | null;
}>();

const remaining = ref<number | null>(null);

function calcRemaining() {
  if (!props.coupon?.dateTo) return null;
  const diff = new Date(props.coupon.dateTo).getTime() - Date.now();
  return diff > 0 ? diff : 0;
}

function formatCountdown(ms: number) {
  const totalSeconds = Math.floor(ms / 1000);
  const days = Math.floor(totalSeconds / 86400);
  const hours = Math.floor((totalSeconds % 86400) / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;
  const hh = String(hours).padStart(2, "0");
  const mm = String(minutes).padStart(2, "0");
  const ss = String(seconds).padStart(2, "0");
  return `${days} dni ${hh}:${mm}:${ss}`;
}

const { pause } = useIntervalFn(
  () => {
    const r = calcRemaining();
    remaining.value = r;
    if (r === 0) pause();
  },
  1000,
  { immediateCallback: true },
);

onMounted(() => {
  remaining.value = calcRemaining();
});

const countdownText = computed(() => {
  if (remaining.value === null || remaining.value === 0) return "";
  return formatCountdown(remaining.value);
});

const reductionLabel = computed(() =>
  props.coupon ? formatCouponReduction(props.coupon) : "",
);
</script>

<template>
  <div class="promo-overlay">
    <p v-if="coupon" class="promo-overlay__row promo-overlay__row--code">
      <strong>{{ reductionLabel }}</strong>
      <span
        >z kodem:
        <strong class="promo-overlay__code">{{ coupon.code }}</strong></span
      >
    </p>
    <p
      v-if="coupon && countdownText"
      class="promo-overlay__row promo-overlay__row--time"
    >
      <span>Promocja kończy się za:</span>
      <strong class="promo-overlay__time">{{ countdownText }}</strong>
    </p>
  </div>
</template>

<style scoped>
.promo-overlay {
  position: absolute;
  left: 12px;
  right: 12px;
  bottom: 12px;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 4px;
  color: var(--color-black, #151515);
  z-index: 3;
  pointer-events: none;
}

.promo-overlay__row {
  display: inline-flex;
  flex-wrap: wrap;
  align-items: baseline;
  gap: 6px;
  margin: 0;
  padding: 3px 6px;
  background: #ffffff;
  border-radius: 4px;
  font-size: 0.875rem;
  font-weight: 400;
  line-height: 1.2;
}

.promo-overlay__row strong {
  font-weight: 700;
}

.promo-overlay__code {
  letter-spacing: 0.5px;
}

.promo-overlay__time {
  color: var(--color-sale, #f83e3e);
}
</style>
